import { AxiosResponse } from 'axios';
import apiClient from '../api/apiClient';
import {
  ApiResponse,
  LectureCategory,
  VimeoInfo,
  LectureListResponse,
  LectureCreateRequest,
  LectureCreateResponse,
  LectureUpdateRequest,
  LectureFileListResponse,
  LectureFileUploadResponse,
  AdminLectureListParams,
  AdminLectureListResponse,
  Lecturer,
  LectureUpdateRequestV2,
  LectureDetail,
  AdminLectureDetail,
} from '../../types/admin';

// ===== 강의 큐레이션 응답 타입 (신규 스키마) =====
export interface ClassLecturerInfo {
  id: number;
  email: string;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

export interface ClassRelatedLecture {
  relation_id: number;
  lecture_id: number;
  title: string;
  thumbnail_url: string;
  // 서버에서 문자열로 올 수도 있어 스펙에 맞춰 string 허용
  tags: string | string[];
  lecturer_info: ClassLecturerInfo;
}

export interface ClassCurationSection {
  lecture_section_id: number;
  title: string;
  text: string;
  related_lectures: ClassRelatedLecture[];
}

export interface ClassCurationListResponse {
  class_sections: ClassCurationSection[];
}

// ===== 강의 카테고리 관련 API =====

// 강의 카테고리 목록 조회
export const getLectureCategories = async (): Promise<LectureCategory[]> => {
  try {

    // 서버 응답 스키마는 optimized_url, order, icon_image_url 등을 포함
    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/api/admin/lecture-categories/list');


    // 프론트 타입(LectureCategory)에 맞게 매핑
    const mapped: LectureCategory[] = (response.data.data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      route_name: item.route_name,
      level: item.level,
      parent_category_id: item.parent_category_id ?? null,
      optimized_image_url: item.optimized_url ?? null,
      sort_order: item.order ?? 0,
      icon_image_url: item.icon_image_url ?? '',
      lecture_count: item.lecture_count ?? 0,
      parent_category_name: item.parent_category_name ?? null
    }));

    return mapped;
  } catch (error: any) {

    let errorMessage = '강의 카테고리 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== Vimeo 관련 API =====

// Vimeo 정보 조회
export const getVimeoInfo = async (vimeoId: string): Promise<VimeoInfo> => {
  try {

    const response: AxiosResponse<ApiResponse<VimeoInfo>> = await apiClient.get(`/api/admin/vimeo/${vimeoId}`);


    return response.data.data;
  } catch (error: any) {

    let errorMessage = 'Vimeo 정보 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = 'Vimeo 정보를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 관련 API =====

// 강의 상세 정보 조회
export const getLectureDetail = async (lectureId: number): Promise<LectureDetail> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureDetail>> = await apiClient.get(`/api/lectures/detail/${lectureId}`);


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 상세 정보 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '접근 권한이 없습니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    }

    throw new Error(errorMessage);
  }
};

// 강의 생성 (임시저장)
export const createLecture = async (lectureData: LectureCreateRequest): Promise<LectureCreateResponse> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureCreateResponse>> = await apiClient.post('/api/admin/lectures', lectureData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 생성에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 수정 (모든 정보 저장)
export const updateLecture = async (id: number, lectureData: LectureUpdateRequest): Promise<void> => {
  try {

    const response = await apiClient.put(`/api/admin/lectures/${id}`, lectureData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

  } catch (error: any) {

    let errorMessage = '강의 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 삭제
export const deleteLecture = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/lectures/${id}`);

  } catch (error: any) {

    let errorMessage = '강의 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 이미지 관련 API =====

// 썸네일 수정
export const updateLectureThumbnail = async (id: number, thumbnailImage: File): Promise<{ thumbnail_url: string }> => {
  try {

    const formData = new FormData();
    formData.append('thumbnailImage', thumbnailImage);

    const response = await apiClient.put(`/api/admin/lectures/${id}/thumbnail`, formData, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

    
    // 응답에서 thumbnail_url 반환
    return { thumbnail_url: response.data.data.thumbnail_url };
  } catch (error: any) {

    let errorMessage = '강의 썸네일 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 썸네일 삭제
export const deleteLectureThumbnail = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/lectures/${id}/thumbnail`, {
      headers: {
        'accept': 'application/json',
      },
    });

  } catch (error: any) {

    let errorMessage = '강의 썸네일 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 이미지 수정
export const updateLectureBanner = async (id: number, bannerImage: File): Promise<{ banner_url: string }> => {
  try {

    const formData = new FormData();
    formData.append('bannerImage', bannerImage);

    const response = await apiClient.put(`/api/admin/lectures/${id}/banner`, formData, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

    
    // 응답에서 banner_url 반환 (서버가 반환하는 구조에 따라 조정 필요)
    return { banner_url: response.data.data.banner_url || response.data.data.url };
  } catch (error: any) {

    let errorMessage = '강의 배너 이미지 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 이미지 삭제
export const deleteLectureBanner = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/lectures/${id}/banner`, {
      headers: {
        'accept': 'application/json',
      },
    });

  } catch (error: any) {

    let errorMessage = '강의 배너 이미지 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 목록 관련 API =====

// 강의 목록 조회 (추천 강의 선택용)
export const getLectureList = async (page: number = 0, size: number = 20): Promise<LectureListResponse> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureListResponse>> = await apiClient.get('/api/lectures/info/list', {
      params: { page, size }
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 파일 관련 API =====

// 강의 파일 다중 업로드
export const uploadLectureFiles = async (lectureId: number, files: File[]): Promise<LectureFileUploadResponse> => {
  try {

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response: AxiosResponse<ApiResponse<LectureFileUploadResponse>> = await apiClient.post(`/api/lecture-files/${lectureId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의자료 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의자료 삭제
export const deleteLectureFile = async (lectureId: number, lectureFileId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/lecture-files/${lectureId}/${lectureFileId}`);

  } catch (error: any) {

    let errorMessage = '강의자료 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의 또는 파일을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 파일 목록 조회
export const getLectureFiles = async (lectureId: number): Promise<LectureFileListResponse> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureFileListResponse>> = await apiClient.get(`/api/lecture-files/list/${lectureId}`);


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의자료 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 배너 관련 API =====

// 배너 목록 조회
export const getBannerList = async (): Promise<any[]> => {
  try {

    const response: AxiosResponse<ApiResponse<any[]>> = await apiClient.get('/api/admin/ui/class/banner/list');


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '배너 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 생성
export const createBanner = async (bannerData: {
  title: string;
  description: string;
  imageFile: File;
  fontColorHexCode: string;
}): Promise<{ id: number }> => {
  try {

    const formData = new FormData();
    formData.append('title', bannerData.title);
    formData.append('description', bannerData.description);
    formData.append('imageFile', bannerData.imageFile);
    formData.append('fontColorHexCode', bannerData.fontColorHexCode);

    const response: AxiosResponse<ApiResponse<{ id: number }>> = await apiClient.post('/api/admin/ui/class/banner', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '배너 생성에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 수정
export const updateBanner = async (id: number, bannerData: {
  title: string;
  description: string;
  imageFile?: File;
  fontColorHexCode: string;
}): Promise<void> => {
  try {

    const formData = new FormData();
    formData.append('title', bannerData.title);
    formData.append('description', bannerData.description);
    formData.append('fontColorHexCode', bannerData.fontColorHexCode);
    if (bannerData.imageFile) {
      formData.append('imageFile', bannerData.imageFile);
    }

    const response = await apiClient.put(`/api/admin/ui/class/banner/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  } catch (error: any) {

    let errorMessage = '배너 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '배너를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 삭제
export const deleteBanner = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/ui/class/banner/${id}`);

  } catch (error: any) {

    let errorMessage = '배너 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '배너를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 카테고리 수정
export const updateLectureCategory = async (
  categoryId: number,
  data: {
    categoryName: string;
    categoryRouteName: string;
    parentCategoryId?: number;
    sortOrder: number;
    optimizedImage?: File;
    iconImage?: File;
  }
): Promise<any> => {
  try {

    const formData = new FormData();
    formData.append('categoryName', data.categoryName);
    formData.append('categoryRouteName', data.categoryRouteName);
    formData.append('sortOrder', data.sortOrder.toString());
    
    if (data.parentCategoryId !== undefined) {
      formData.append('parentCategoryId', data.parentCategoryId.toString());
    }
    
    if (data.optimizedImage) {
      formData.append('optimizedImage', data.optimizedImage);
    }
    
    if (data.iconImage) {
      formData.append('iconImage', data.iconImage);
    }

    const response: AxiosResponse<ApiResponse<any>> = await apiClient.put(
      `/api/admin/lecture-categories/${categoryId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '카테고리 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 카테고리를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 카테고리 생성
export const createLectureCategory = async (data: {
  categoryName: string;
  categoryRouteName: string;
  parentCategoryId?: number;
  sortOrder: number;
  optimizedImage?: File;
  iconImage?: File;
}): Promise<any> => {
  try {

    const formData = new FormData();
    formData.append('categoryName', data.categoryName);
    formData.append('categoryRouteName', data.categoryRouteName);
    formData.append('sortOrder', data.sortOrder.toString());
    
    if (data.parentCategoryId !== undefined) {
      formData.append('parentCategoryId', data.parentCategoryId.toString());
    }
    
    if (data.optimizedImage) {
      formData.append('optimizedImage', data.optimizedImage);
    }
    
    if (data.iconImage) {
      formData.append('iconImage', data.iconImage);
    }

    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post(
      '/api/admin/lecture-categories',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '카테고리 생성에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 409) {
      errorMessage = '이미 존재하는 카테고리명 또는 슬러그입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 카테고리 삭제
export const deleteLectureCategory = async (categoryId: number): Promise<any> => {
  try {

    const response: AxiosResponse<ApiResponse<any>> = await apiClient.delete(
      `/api/admin/lecture-categories/${categoryId}`
    );


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '카테고리 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 카테고리를 찾을 수 없습니다.';
    } else if (error.response.status === 409) {
      errorMessage = '해당 카테고리에 강의가 있어 삭제할 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 큐레이션 관련 API =====

// 강의 큐레이션 목록 조회 (신규 응답 스키마 반영)
export const getLectureCurationList = async (): Promise<ClassCurationSection[]> => {
  try {

    const response: AxiosResponse<ApiResponse<ClassCurationListResponse>> = await apiClient.get('/api/admin/ui/class/curations/list');


    // API 응답에서 class_sections 배열 반환
    return response.data.data.class_sections || [];
  } catch (error: any) {

    let errorMessage = '강의 큐레이션 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션 섹션 생성
export const createLectureCurationSection = async (curationData: {
  title: string;
  description: string;
}): Promise<{ id: number }> => {
  try {

    const response: AxiosResponse<ApiResponse<{ id: number }>> = await apiClient.post('/api/admin/ui/class/curations', curationData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 생성에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션 섹션 수정
export const updateLectureCurationSection = async (id: number, curationData: {
  title: string;
  description: string;
}): Promise<void> => {
  try {

    const response = await apiClient.put(`/api/admin/ui/class/curations/${id}`, curationData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션 섹션 삭제
export const deleteLectureCurationSection = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/ui/class/curations/${id}`);

  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의를 큐레이션에 추가
export const addLectureToCuration = async (sectionId: number, lectureId: number): Promise<void> => {
  try {
    const response = await apiClient.post('/api/admin/ui/class/curations/related', { sectionId, lectureId });

  } catch (error: any) {

    let errorMessage = '강의를 큐레이션에 추가하는데 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '큐레이션 섹션이나 강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의를 큐레이션에서 제거
export const removeLectureFromCuration = async (curationId: number, lectureId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/admin/ui/class/curations/related/${curationId}/${lectureId}`);

  } catch (error: any) {

    let errorMessage = '강의를 큐레이션에서 제거하는데 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '큐레이션 섹션이나 강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 관리 관련 API =====

// 강의 목록 조회 (관리자용)
export const getAdminLectures = async (params: AdminLectureListParams): Promise<AdminLectureListResponse> => {
  try {

    // 쿼리 파라미터 변환
    const queryParams: any = {
      page: params.page,
      size: params.size
    };

    // 날짜 필터링 (from, to 파라미터로 변환)
    if (params.dateFrom) {
      queryParams.from = params.dateFrom;
    }
    if (params.dateTo) {
      queryParams.to = params.dateTo;
    }
    // 기존 createdAt 필드도 지원 (하위 호환성)
    if (params.createdAt && !params.dateFrom && !params.dateTo) {
      const date = new Date(params.createdAt);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      queryParams.from = `${year}-${month}-${day}`;
      queryParams.to = `${year}-${month}-${day}`;
    }

    // 기타 필터들
    if (params.categoryId) queryParams.categoryId = params.categoryId;
    if (params.lecturerId) queryParams.lecturerId = params.lecturerId;
    if (params.status !== undefined) queryParams.status = params.status;
    if (params.search) queryParams.search = params.search;

    const response: AxiosResponse<ApiResponse<AdminLectureListResponse>> = await apiClient.get('/api/admin/lectures/list', {
      params: queryParams
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강연자 목록 조회
export const getLecturers = async (): Promise<Lecturer[]> => {
  try {

    const response: AxiosResponse<ApiResponse<Lecturer[]>> = await apiClient.get('/api/persons/list/lecturers');


    const mapped = (response.data.data || []).map((item: Lecturer & { department?: string }) => ({
      ...item,
      introduction: item?.department ?? (item as any)?.department ?? '',
    }));

    return mapped;
  } catch (error: any) {

    let errorMessage = '강연자 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 수정 (PUT API - 모든 정보 업데이트)
export const updateLectureV2 = async (id: number, lectureData: LectureUpdateRequestV2): Promise<void> => {
  try {

    const response = await apiClient.put(`/api/admin/lectures/${id}`, lectureData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

  } catch (error: any) {

    let errorMessage = '강의 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};


// 카테고리 순서 업데이트
export const updateCategoryOrder = async (categoryId: number, newOrder: number): Promise<void> => {
  try {

    const response = await apiClient.put(
      `/api/admin/lecture-categories/${categoryId}/order`,
      { sortOrder: newOrder },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

  } catch (error: any) {

    let errorMessage = '카테고리 순서 업데이트에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 카테고리를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 관리자 전용 강의 API =====

// 관리자용 강의 상세 정보 조회
export const getAdminLectureDetail = async (lectureId: number): Promise<AdminLectureDetail> => {
  try {

    const response: AxiosResponse<ApiResponse<AdminLectureDetail>> = await apiClient.get(`/api/admin/lectures/${lectureId}`);


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '관리자 강의 상세 정보 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    }

    throw new Error(errorMessage);
  }
};

// 강의 소개 이미지 업로드
export const updateLectureIntroduction = async (lectureId: number, imageFile: File): Promise<{ introduction_url: string }> => {
  try {

    const formData = new FormData();
    formData.append('introductionImage', imageFile);

    const response = await apiClient.put(`/api/admin/lectures/${lectureId}/introduction`, formData, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

    
    // 응답에서 URL 반환
    return { introduction_url: response.data.data?.introduction || response.data.data?.url || '' };
  } catch (error: any) {

    let errorMessage = '강의 소개 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강사 소개 이미지 업로드
export const updateLecturerIntroduction = async (lectureId: number, imageFile: File): Promise<void> => {
  try {

    const formData = new FormData();
    formData.append('lecturerIntroductionImage', imageFile);

    const response = await apiClient.put(`/api/admin/lectures/${lectureId}/lecturer-introduction`, formData, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

  } catch (error: any) {

    let errorMessage = '강사 소개 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 수강 후기 이미지 업로드
export const updateLectureReview = async (lectureId: number, imageFile: File): Promise<void> => {
  try {

    const formData = new FormData();
    formData.append('reviewImage', imageFile);

    const response = await apiClient.put(`/api/admin/lectures/${lectureId}/review`, formData, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

  } catch (error: any) {

    let errorMessage = '수강 후기 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 카테고리 순서 일괄 업데이트 (순차적 처리)
export const updateCategoriesOrder = async (categories: { id: number; sortOrder: number; name?: string; slug?: string }[]): Promise<void> => {
  try {

    const results = [];
    const errors = [];

    // 순차적으로 하나씩 처리
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      
      try {
        
        // name과 slug가 제공되지 않은 경우, 기존 카테고리 정보 조회
        let requestBody;
        
        if (category.name && category.slug) {
          // 이미 name과 slug가 있는 경우 그대로 사용
          requestBody = {
            name: category.name,
            slug: category.slug,
            sortOrder: category.sortOrder
          };
        } else {
          // 기존 카테고리 정보 조회
          try {
            const existingResponse = await apiClient.get(`/api/admin/study-categories/${category.id}`);
            const existingData = existingResponse.data.data || existingResponse.data;
            
            requestBody = {
              name: existingData.name,
              slug: existingData.slug || existingData.route_name,
              sortOrder: category.sortOrder
            };
            
          } catch (fetchError: any) {
            if (fetchError.response?.status === 404) {
              console.warn(`카테고리 ${category.id}가 존재하지 않습니다. 건너뜀.`);
              errors.push({
                id: category.id,
                success: false,
                status: 404,
                message: '카테고리가 존재하지 않습니다.'
              });
              continue;
            }
            throw fetchError;
          }
        }
        
        const response = await apiClient.put(
          `/api/admin/study-categories/${category.id}`,
          requestBody
        );
        
        results.push({
          id: category.id,
          success: true,
          data: response.data
        });
        
        
        // 서버 부하 방지를 위한 짧은 대기
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error: any) {
        const errorInfo = {
          id: category.id,
          success: false,
          status: error.response?.status,
          message: error.response?.data?.message || error.message
        };
        
        errors.push(errorInfo);
        
        console.warn(`카테고리 ${category.id} 업데이트 실패:`, errorInfo);
        
        // 404나 409 에러는 개별 카테고리 문제이므로 계속 진행
        if (error.response?.status === 404) {
          console.warn(`카테고리 ${category.id}가 존재하지 않습니다. 건너뜀.`);
        } else if (error.response?.status === 409) {
          console.warn(`카테고리 ${category.id}에서 충돌이 발생했습니다. 건너뜀.`);
        } else {
          // 다른 에러는 전체 프로세스를 중단할 수 있음
          throw error;
        }
      }
    }
    
    
    // 성공한 것이 하나도 없으면 에러
    if (results.length === 0) {
      throw new Error('모든 카테고리 업데이트가 실패했습니다.');
    }
    
    // 일부 실패한 경우 경고 메시지
    if (errors.length > 0) {
      console.warn(`${errors.length}개 카테고리 업데이트가 실패했지만, ${results.length}개는 성공했습니다.`);
    }
    
  } catch (error: any) {

    let errorMessage = '카테고리 순서 일괄 업데이트에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '해당 카테고리를 찾을 수 없습니다.';
    } else if (error.response.status === 409) {
      errorMessage = '카테고리 업데이트 중 충돌이 발생했습니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.status === 415) {
      errorMessage = '요청 형식이 올바르지 않습니다. Content-Type을 확인해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};
